/**
 * MAX AET AI Academy - Integrated Platform
 * Combines: AI Lexikon + Vibe Analyzer + MCG Integration
 * 
 * @version 3.0.0
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import type {
  Language,
  LexiconItem,
  FilterState,
  ViewMode,
} from "./types";
import { DEFAULT_FILTER_STATE, ALPHABET } from "./types";
import { lexicon, getStatistics, searchLexicon } from "./data/lexicon";

// Components
import StatsBar from "./components/StatsBar";
import FilterBar from "./components/FilterBar";
import LetterNavigation from "./components/LetterNavigation";
import LexikonGrid from "./components/LexikonGrid";
import DetailModal from "./components/DetailModal";
import Footer from "./components/Footer";

// Hooks
import { useLocalStorage } from "./hooks/useLocalStorage";
import { useDeepLink } from "./hooks/useDeepLink";
import { useKeyboard } from "./hooks/useKeyboard";

// App Modes
type AppMode = "lexikon" | "vibe";

/**
 * Main Application Component - MAX AET Platform
 */
const App: React.FC = () => {
  // === State Management ===
  
  // App mode (Lexikon or Vibe Analyzer)
  const [mode, setMode] = useLocalStorage<AppMode>("max-aet-mode", "lexikon");
  
  // Persisted preferences
  const [language, setLanguage] = useLocalStorage<Language>("max-aet-lang", "cs");
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>("max-aet-view", "grid");
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTER_STATE);
  
  // UI state
  const [selectedItem, setSelectedItem] = useState<LexiconItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Deep linking
  const { getItemFromHash, setHash } = useDeepLink(lexicon);
  
  // === Computed Values ===
  
  // Statistics
  const statistics = useMemo(() => getStatistics(), []);
  
  // Available categories
  const categories = useMemo(() => {
    const cats = new Set(lexicon.map((item) => item.category));
    return Array.from(cats).sort();
  }, []);
  
  // Letter counts
  const letterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ALPHABET.forEach((letter) => {
      counts[letter] = lexicon.filter(
        (item) => item.mcgLetter.toUpperCase() === letter
      ).length;
    });
    return counts;
  }, []);
  
  // Filtered items
  const filteredItems = useMemo(() => {
    let items = [...lexicon];
    
    // Search filter
    if (filters.searchQuery.trim()) {
      items = searchLexicon(filters.searchQuery, language);
    }
    
    // Letter filter
    if (filters.selectedLetter) {
      items = items.filter(
        (item) => item.mcgLetter.toUpperCase() === filters.selectedLetter
      );
    }
    
    // Level filter
    if (filters.selectedLevels.length > 0) {
      items = items.filter((item) =>
        filters.selectedLevels.includes(item.level)
      );
    }
    
    // Category filter
    if (filters.selectedCategories.length > 0) {
      items = items.filter((item) =>
        filters.selectedCategories.includes(item.category as any)
      );
    }
    
    // Pillar filter
    if (filters.selectedPillars.length > 0) {
      items = items.filter((item) =>
        filters.selectedPillars.includes(item.pillar)
      );
    }
    
    // Has minicourse filter
    if (filters.hasMiniCourse === true) {
      items = items.filter(
        (item) => item.miniCourses && item.miniCourses.length > 0
      );
    } else if (filters.hasMiniCourse === false) {
      items = items.filter(
        (item) => !item.miniCourses || item.miniCourses.length === 0
      );
    }
    
    // Sort alphabetically by term in current language
    items.sort((a, b) => {
      const termA = a[language].term.toLowerCase();
      const termB = b[language].term.toLowerCase();
      return termA.localeCompare(termB, language === "cs" ? "cs" : "en");
    });
    
    return items;
  }, [filters, language]);
  
  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.searchQuery.trim()) count++;
    if (filters.selectedLetter) count++;
    if (filters.selectedLevels.length > 0) count++;
    if (filters.selectedCategories.length > 0) count++;
    if (filters.selectedPillars.length > 0) count++;
    if (filters.hasMiniCourse !== null) count++;
    return count;
  }, [filters]);
  
  // === Event Handlers ===
  
  const handleFilterChange = useCallback((updates: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);
  
  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTER_STATE);
  }, []);
  
  const handleItemClick = useCallback((item: LexiconItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setHash(item.id);
  }, [setHash]);
  
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setHash(null);
  }, [setHash]);
  
  const handleNavigateToItem = useCallback((id: string) => {
    const item = lexicon.find((i) => i.id === id);
    if (item) {
      setSelectedItem(item);
      setHash(item.id);
    }
  }, [setHash]);
  
  const handleShare = useCallback((item: LexiconItem) => {
    const url = `${window.location.origin}${window.location.pathname}#${item.id}`;
    
    if (navigator.share) {
      navigator.share({
        title: item[language].term,
        text: item[language].shortDefinition,
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
    }
  }, [language]);
  
  // === Effects ===
  
  // Handle deep link on mount
  useEffect(() => {
    const item = getItemFromHash();
    if (item) {
      setSelectedItem(item);
      setIsModalOpen(true);
    }
  }, [getItemFromHash]);
  
  // Update document language
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  
  // Keyboard shortcuts
  useKeyboard({
    onEscape: handleCloseModal,
    onSlash: () => {
      const searchInput = document.querySelector<HTMLInputElement>(".search-input");
      searchInput?.focus();
    },
  });

  // Labels
  const labels = {
    cs: {
      lexikon: "LEXIKON",
      vibe: "VIBE",
      vibeComingSoon: "Vibe Analyzer připravujeme",
      vibeDescription: "AI generovaná estetika, barevné palety a vizualizace budou brzy dostupné.",
    },
    en: {
      lexikon: "LEXIKON",
      vibe: "VIBE",
      vibeComingSoon: "Vibe Analyzer coming soon",
      vibeDescription: "AI-generated aesthetics, color palettes and visualizations will be available soon.",
    },
  };

  const t = labels[language];
  
  // === Render ===
  
  return (
    <div className="bg-void" style={{ minHeight: "100vh" }}>
      {/* Background Effects */}
      <div className="bg-ambient" />
      <div className="bg-noise" />
      
      {/* Header */}
      <header className="header">
        <div className="header-logo">
          <h1 className="header-logo-title">MAX AET</h1>
          <p className="header-logo-subtitle">
            Professional AI Vibe Lexikon // Integrated
          </p>
        </div>
        
        <div className="header-nav">
          {/* Mode Tabs */}
          <div className="nav-tabs">
            <button
              className={`nav-tab ${mode === "lexikon" ? "active" : ""}`}
              onClick={() => setMode("lexikon")}
            >
              📚 {t.lexikon}
            </button>
            <button
              className={`nav-tab ${mode === "vibe" ? "active" : ""}`}
              onClick={() => setMode("vibe")}
            >
              🎨 {t.vibe}
            </button>
          </div>
          
          {/* Language Toggle */}
          <div className="language-toggle">
            <button
              className={`language-toggle-btn ${language === "cs" ? "active" : ""}`}
              onClick={() => setLanguage("cs")}
            >
              CZ
            </button>
            <button
              className={`language-toggle-btn ${language === "en" ? "active" : ""}`}
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
          </div>
        </div>
        
        <div className="header-status">
          <span className="status-dot" />
          SYSTEM ONLINE
        </div>
      </header>
      
      {/* Main Content */}
      {mode === "lexikon" ? (
        <>
          <StatsBar statistics={statistics} language={language} />
          
          <main>
            <FilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              categories={categories}
              statistics={statistics}
              language={language}
              activeFilterCount={activeFilterCount}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
            
            <LetterNavigation
              letters={ALPHABET}
              activeLetter={filters.selectedLetter}
              letterCounts={letterCounts}
              onSelect={(letter) => handleFilterChange({ selectedLetter: letter })}
              language={language}
            />
            
            <div className="lexikon-container">
              <LexikonGrid
                items={filteredItems}
                language={language}
                viewMode={viewMode}
                onItemClick={handleItemClick}
                searchQuery={filters.searchQuery}
                emptyMessage={
                  language === "cs"
                    ? "Žádné pojmy neodpovídají vašemu hledání"
                    : "No terms match your search"
                }
              />
            </div>
          </main>
          
          {isModalOpen && selectedItem && (
            <DetailModal
              item={selectedItem}
              language={language}
              onClose={handleCloseModal}
              onNavigate={handleNavigateToItem}
              onShare={handleShare}
            />
          )}
        </>
      ) : (
        /* Vibe Analyzer Mode - Coming Soon Placeholder */
        <main style={{ padding: "80px 24px", textAlign: "center" }}>
          <div className="glass-panel" style={{ 
            maxWidth: "600px", 
            margin: "0 auto", 
            padding: "60px 40px" 
          }}>
            <div style={{ fontSize: "64px", marginBottom: "24px" }}>🎨</div>
            <h2 className="font-heading" style={{ 
              fontSize: "32px", 
              marginBottom: "16px",
              color: "var(--text-primary)"
            }}>
              {t.vibeComingSoon}
            </h2>
            <p className="font-mono" style={{ 
              color: "var(--text-muted)",
              fontSize: "14px",
              lineHeight: "1.8"
            }}>
              {t.vibeDescription}
            </p>
            <div style={{ 
              marginTop: "32px",
              display: "flex",
              justifyContent: "center",
              gap: "16px"
            }}>
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "var(--neon-primary)",
                boxShadow: "0 0 20px rgba(176, 38, 255, 0.4)"
              }} />
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "var(--neon-secondary)",
                boxShadow: "0 0 20px rgba(0, 240, 255, 0.4)"
              }} />
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "var(--neon-tertiary)",
                boxShadow: "0 0 20px rgba(255, 45, 106, 0.4)"
              }} />
            </div>
          </div>
        </main>
      )}
      
      <Footer language={language} />
    </div>
  );
};

export default App;
